// onchain.ts — Phase 7 §7.4 on-chain reads for the web frontend.
//
// Provides:
//   - publicClient: viem PublicClient for the configured chain
//   - fetchUsdcBalance(addr): reads USDC.balanceOf — pure async fn
//   - fetchTokenSaleStatus(addr): reads ~6 view fns in one multicall
//   - useUsdcBalance(addr): React hook wrapping fetchUsdcBalance with
//     a 15s TTL cache so multiple components asking for the same
//     address share one round-trip
//   - useTokenSaleStatus(addr): same pattern for sale state
//   - useTotalRaisedAcrossSales(): aggregates totalRaised across
//     known restaurant sales for the sidebar TVL banner
//
// Design notes:
//   - Read-only. Writes (approve, buy, sign) flow through the
//     existing tx-sign/payment widget paths against the user's
//     Privy-managed Smart Wallet — Phase 7 doesn't change that.
//   - Cache: module-level Map<key, { value, fetchedAt }>. TTL 15s.
//     Concurrent in-flight reads share a single Promise (request
//     coalescing). Cache key = `${chainEnv}:${kind}:${addr}` so
//     swapping VITE_CHAIN_ENV at build time gives a clean slate.
//   - SSR-safe: no `window` access at module scope. `useEffect`
//     gates DOM-bound side effects.
//   - viem auto-retries transient RPC errors (~3x). Hard failures
//     surface to the hook as `{ error }`.

import { useEffect, useState, useCallback } from 'react';
import { createPublicClient, http, type Address, type Chain } from 'viem';
import { baseSepolia, foundry } from 'viem/chains';
// Browser-safe ABIs only — `@rmint/core/contracts/client` evaluates
// `process.env.*` at module top level (Next.js v6 carry-over) which crashes
// under Vite. `abis.ts` is pure constants. `chains.ts` is also pure
// (factory function, no top-level env access).
import {
  erc20ReadAbi,
  tokenSaleAbi,
} from '@rmint/core/contracts/abis';
import { tenderlyBaseFork } from '@rmint/core/contracts/chains';
import { env } from '@rmint/shell';

// ─── Chain selection ─────────────────────────────────────────────

function chainFor(env: { chainEnv: string; rpcUrl: string }): Chain {
  switch (env.chainEnv) {
    case 'local':
      return foundry;
    case 'base':
      return tenderlyBaseFork(env.rpcUrl);
    case 'base-sepolia':
    default:
      return baseSepolia;
  }
}

export const publicClient = createPublicClient({
  chain: chainFor(env),
  transport: http(env.rpcUrl, { batch: true }),
  batch: { multicall: true },
  cacheTime: 4_000,
});

// ─── Address resolution ──────────────────────────────────────────
//
// Vite cannot run packages/core/src/contracts/addresses.ts cleanly
// because that file uses `process.env.NEXT_PUBLIC_*` (Next.js
// convention). The web app instead imports the deployment JSONs
// directly — same source of truth, just resolved on the browser
// side. JSON imports are native in Vite + tsc.

import baseSepoliaJson from '../../../../deployments/base-sepolia.json';
import localJson from '../../../../deployments/local.json';
import tenderlyJson from '../../../../deployments/tenderly.json';

type DeploymentJson = {
  chainId: number;
  network: string;
  contracts: Record<string, string>;
};

function pickDeployment(): DeploymentJson {
  switch (env.chainEnv) {
    case 'local':
      return localJson as DeploymentJson;
    case 'base':
      return tenderlyJson as DeploymentJson;
    case 'base-sepolia':
    default:
      return baseSepoliaJson as DeploymentJson;
  }
}

const ZERO: Address = '0x0000000000000000000000000000000000000000';

function isHexAddress(v: string | undefined): v is `0x${string}` {
  return !!v && /^0x[a-fA-F0-9]{40}$/.test(v);
}

function readAddress(deployment: DeploymentJson, key: string): Address {
  const raw = deployment.contracts[key];
  return isHexAddress(raw) ? (raw as Address) : ZERO;
}

const deployment = pickDeployment();
export const onchainAddresses = {
  usdc: readAddress(deployment, 'MockUSDC'),
  sakuraSale: readAddress(deployment, 'SakuraSale'),
  sakuraToken: readAddress(deployment, 'SakuraToken'),
};

// ─── Pure async readers ──────────────────────────────────────────

/**
 * Read USDC balance for `walletAddress`. Returns the raw bigint
 * balance and the token's `decimals` so callers can format. USDC is
 * 6 decimals on Base; we fetch decimals defensively.
 */
export async function fetchUsdcBalance(
  walletAddress: Address,
): Promise<{ balance: bigint; decimals: number }> {
  if (onchainAddresses.usdc === ZERO) {
    return { balance: 0n, decimals: 6 };
  }
  const [balance, decimals] = await publicClient.multicall({
    contracts: [
      {
        address: onchainAddresses.usdc,
        abi: erc20ReadAbi,
        functionName: 'balanceOf',
        args: [walletAddress],
      },
      {
        address: onchainAddresses.usdc,
        abi: erc20ReadAbi,
        functionName: 'decimals',
      },
    ],
    allowFailure: false,
  });
  return { balance: balance as bigint, decimals: Number(decimals) };
}

export type TokenSaleStatus = {
  totalRaised: bigint;
  softCap: bigint;
  hardCap: bigint;
  finalized: boolean;
  refundsEnabled: boolean;
  startTime: bigint;
  endTime: bigint;
  minPurchase: bigint;
  maxPurchase: bigint;
  remainingAllocation: bigint;
};

/**
 * Read sale-contract state in one multicall. ~6 field round-trips
 * collapse into one RPC request via Multicall3 (canonical at
 * 0xcA11bde05977b3631167028862bE2a173976CA11 on Base + sepolia).
 */
export async function fetchTokenSaleStatus(
  saleAddress: Address,
): Promise<TokenSaleStatus> {
  const calls = [
    'totalRaised',
    'softCap',
    'hardCap',
    'finalized',
    'refundsEnabled',
    'startTime',
    'endTime',
    'minPurchase',
    'maxPurchase',
    'remainingAllocation',
  ] as const;

  const results = await publicClient.multicall({
    contracts: calls.map((fn) => ({
      address: saleAddress,
      abi: tokenSaleAbi,
      functionName: fn,
    })),
    allowFailure: false,
  });

  return {
    totalRaised: results[0] as bigint,
    softCap: results[1] as bigint,
    hardCap: results[2] as bigint,
    finalized: results[3] as boolean,
    refundsEnabled: results[4] as boolean,
    startTime: results[5] as bigint,
    endTime: results[6] as bigint,
    minPurchase: results[7] as bigint,
    maxPurchase: results[8] as bigint,
    remainingAllocation: results[9] as bigint,
  };
}

// ─── TTL cache + request coalescing ──────────────────────────────
//
// Module-level cache keyed by `${chainEnv}:${kind}:${addr}`. Two
// goals:
//   1. Multiple components asking for the same address get one
//      round-trip (request coalescing via shared Promise).
//   2. Within TTL_MS, no re-fetch — useful for back-to-back mounts
//      (StrictMode double-invoke, route swaps).

const TTL_MS = 15_000;

type CacheEntry<T> = {
  value?: T;
  fetchedAt?: number;
  inflight?: Promise<T>;
};

const cache = new Map<string, CacheEntry<unknown>>();

function cacheKey(kind: string, addr: string): string {
  return `${env.chainEnv}:${kind}:${addr.toLowerCase()}`;
}

async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
): Promise<T> {
  const now = Date.now();
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (entry?.value !== undefined && entry.fetchedAt && now - entry.fetchedAt < TTL_MS) {
    return entry.value;
  }
  if (entry?.inflight) return entry.inflight;
  const promise = fetcher().then(
    (v) => {
      cache.set(key, { value: v, fetchedAt: Date.now() });
      return v;
    },
    (err) => {
      // Drop the inflight on failure so retries actually retry.
      cache.delete(key);
      throw err;
    },
  );
  cache.set(key, { inflight: promise });
  return promise;
}

/** Drop a cache entry — for SSE-driven invalidation. Test-only export. */
export function _onchainCacheClear(): void {
  cache.clear();
}

// ─── React hooks ─────────────────────────────────────────────────

export type AsyncResult<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
};

/**
 * Subscribe to USDC balance for the given wallet. Returns
 * `{ data: { balance, decimals } | null, loading, error, refresh }`.
 * `refresh()` invalidates the cache + re-fetches.
 *
 * Returns `data: null` on missing address (skips the RPC). Render
 * code should treat null as "not yet known".
 */
export function useUsdcBalance(
  walletAddress: Address | undefined | null,
): AsyncResult<{ balance: bigint; decimals: number }> {
  const [data, setData] = useState<{ balance: bigint; decimals: number } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    if (walletAddress) {
      cache.delete(cacheKey('usdc-balance', walletAddress));
    }
    setTick((t) => t + 1);
  }, [walletAddress]);

  useEffect(() => {
    if (!walletAddress || !isHexAddress(walletAddress)) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    cachedFetch(cacheKey('usdc-balance', walletAddress), () =>
      fetchUsdcBalance(walletAddress),
    )
      .then((v) => {
        if (!cancelled) setData(v);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [walletAddress, tick]);

  return { data, loading, error, refresh };
}

/**
 * Subscribe to sale-contract status. Returns the structured
 * TokenSaleStatus or null while loading / on missing address.
 */
export function useTokenSaleStatus(
  saleAddress: Address | undefined | null,
): AsyncResult<TokenSaleStatus> {
  const [data, setData] = useState<TokenSaleStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    if (saleAddress) cache.delete(cacheKey('sale-status', saleAddress));
    setTick((t) => t + 1);
  }, [saleAddress]);

  useEffect(() => {
    if (!saleAddress || !isHexAddress(saleAddress)) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    cachedFetch(cacheKey('sale-status', saleAddress), () =>
      fetchTokenSaleStatus(saleAddress),
    )
      .then((v) => {
        if (!cancelled) setData(v);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [saleAddress, tick]);

  return { data, loading, error, refresh };
}

/**
 * Aggregate totalRaised across all known sales. Used by the sidebar
 * TVL banner. Currently iterates the static list of known sale
 * addresses; when restaurants/ becomes a dossier-driven list,
 * source the addresses from there.
 */
export function useTotalRaisedAcrossSales(): AsyncResult<bigint> {
  const sakura = useTokenSaleStatus(
    onchainAddresses.sakuraSale === ZERO ? null : onchainAddresses.sakuraSale,
  );

  const data: bigint | null = sakura.data ? sakura.data.totalRaised : null;
  const loading = sakura.loading;
  const error = sakura.error;
  const refresh = sakura.refresh;

  return { data, loading, error, refresh };
}

// ─── Phase 6C: write hooks ─────────────────────────────────────────────────────
//
// Privy ONLY for the wallet-sign step (custody); viem for everything
// else (encode, wait receipt, decode logs). See
// docs/superpowers/specs/2026-05-09-phase-6c-fund-design.md §P6C-4.

import { encodeFunctionData, type Hash } from 'viem';
import {
  erc20WriteAbi,
  tokenSaleWriteAbi,
} from '@rmint/core/contracts/abis';
import { useSmartWallets } from '@privy-io/react-auth/smart-wallets';

// USDC contract address by chain — Phase 6 sepolia uses MockUSDC.
// Phase N mainnet would use Circle's USDC. Hardcoded for now;
// per-chain registry is Phase 9 cleanup.
const USDC_ADDRESS_BY_CHAIN: Record<string, `0x${string}`> = {
  'base-sepolia': '0x7825d7e27112a6b3bC4de107747912d642162E22',
  'base': '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  'local': '0x7825d7e27112a6b3bC4de107747912d642162E22',
};

function usdcAddressForEnv(chainEnv: string): `0x${string}` {
  return USDC_ADDRESS_BY_CHAIN[chainEnv] ?? USDC_ADDRESS_BY_CHAIN['base-sepolia']!;
}

// ─── Pure encode helpers (testable, no React) ──────────────────────

export function encodeApproveData(
  spender: `0x${string}`,
  amountMicroUsdc: bigint,
): `0x${string}` {
  return encodeFunctionData({
    abi: erc20WriteAbi,
    functionName: 'approve',
    args: [spender, amountMicroUsdc],
  });
}

export function encodePurchaseData(
  amountMicroUsdc: bigint,
  destinationSafe: `0x${string}`,
): `0x${string}` {
  return encodeFunctionData({
    abi: tokenSaleWriteAbi,
    functionName: 'purchase',
    args: [amountMicroUsdc, destinationSafe],
  });
}

// ─── Hooks ──────────────────────────────────────────────────────────

type WriteHookResult = {
  send: () => Promise<Hash>;
  isPending: boolean;
  error: Error | null;
};

// USDC approve via Privy Smart Wallets (ERC-4337 UserOp through the
// Pimlico bundler + paymaster configured in the Privy dashboard).
// User signs with their Privy EOA; the paired smart account is the
// msg.sender on-chain; gas is sponsored — no Base Sepolia ETH needed.
export function useApproveUsdc(
  saleAddress: `0x${string}`,
  amountMicroUsdc: bigint,
  chainEnv: string,
): WriteHookResult {
  const { client } = useSmartWallets();
  const [isPending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function send(): Promise<Hash> {
    if (!client) {
      throw new Error('Smart wallet not ready — Privy Smart Wallets not configured for this user');
    }
    setPending(true);
    setError(null);
    try {
      const data = encodeApproveData(saleAddress, amountMicroUsdc);
      const hash = await client.sendTransaction({
        to: usdcAddressForEnv(chainEnv),
        data,
        value: 0n,
      });
      setPending(false);
      return hash as Hash;
    } catch (e) {
      setError(e as Error);
      setPending(false);
      throw e;
    }
  }

  return { send, isPending, error };
}

// Sale.purchase via Privy Smart Wallets (same Pimlico-sponsored UserOp path).
export function usePurchase(
  saleAddress: `0x${string}`,
  amountMicroUsdc: bigint,
  destinationSafe: `0x${string}`,
): WriteHookResult {
  const { client } = useSmartWallets();
  const [isPending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function send(): Promise<Hash> {
    if (!client) {
      throw new Error('Smart wallet not ready — Privy Smart Wallets not configured for this user');
    }
    setPending(true);
    setError(null);
    try {
      const data = encodePurchaseData(amountMicroUsdc, destinationSafe);
      const hash = await client.sendTransaction({
        to: saleAddress,
        data,
        value: 0n,
      });
      setPending(false);
      return hash as Hash;
    } catch (e) {
      setError(e as Error);
      setPending(false);
      throw e;
    }
  }

  return { send, isPending, error };
}

// USDC allowance read — pure viem (publicClient already exists in this file).
// Returns null while loading; bumps via refetch().
export function useUsdcAllowance(
  owner: `0x${string}` | undefined,
  spender: `0x${string}`,
  chainEnv: string,
): { allowance: bigint | null; loading: boolean; error: Error | null; refetch: () => void } {
  const [allowance, setAllowance] = useState<bigint | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(owner));
  const [error, setError] = useState<Error | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);

  useEffect(() => {
    if (!owner) {
      setLoading(false);
      setAllowance(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = (await publicClient.readContract({
          address: usdcAddressForEnv(chainEnv),
          abi: erc20ReadAbi,
          functionName: 'allowance',
          args: [owner, spender],
        })) as bigint;
        if (!cancelled) {
          setAllowance(result);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e as Error);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [owner, spender, chainEnv, refetchKey]);

  return {
    allowance,
    loading,
    error,
    refetch: () => setRefetchKey((k) => k + 1),
  };
}

// ─── Formatting helpers ──────────────────────────────────────────

/**
 * Format a USDC bigint into a human-readable string with 2 decimal
 * places + thousands separators. e.g. 5_000_000_000n (USDC 6dp) →
 * "$5,000.00".
 *
 * Rounding: half-up at the bigint level. Using `Number.toFixed(2)`
 * on the fractional part rounds within the fraction but doesn't
 * carry into the whole — e.g. 1.999999 → "1.00" instead of "2.00".
 * Doing the round at bigint scale (cents = round(amount / 10^(d-2)))
 * gives correct carry behavior.
 */
export function formatUsdc(amount: bigint, decimals: number = 6): string {
  if (decimals < 2) {
    return `$${amount.toLocaleString('en-US')}.00`;
  }
  const truncDivisor = 10n ** BigInt(decimals - 2);
  const halfRound = truncDivisor / 2n;
  const cents = (amount + halfRound) / truncDivisor; // round half-up
  const dollars = cents / 100n;
  const subDollar = (cents % 100n).toString().padStart(2, '0');
  return `$${dollars.toLocaleString('en-US')}.${subDollar}`;
}
